import React from 'react';
import flow from 'lodash/flow';
import Button from '@atlaskit/button';
import LockClosedIcon from '@atlaskit/icon/core/lock-locked';
import LockOpenIcon from '@atlaskit/icon/core/lock-unlocked';
import { token } from '@atlaskit/tokens';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { messages as customerAccessMessages } from '@atlassian/jira-servicedesk-customer-permissions/src/common/messages.tsx';
import {
	CUSTOMER_ACCESS,
	type CustomerAccess,
} from '@atlassian/jira-servicedesk-customer-permissions/src/common/types.tsx';
import {
	OpenIcon,
	RestrictedIcon,
} from '@atlassian/jira-servicedesk-customer-permissions/src/common/ui/customer-access-dropdown/icons/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ChannelStatusLozenge, LOZENGE_TYPES } from '../../status-lozenge/index.tsx';
import { BaseChannelItem, ChannelItemImage } from '../index.tsx';
import messages from './messages.tsx';

type Props = {
	accessType: CustomerAccess;
	isAdmin: boolean;
	link: string;
	onClick: () => void;
};

const openImage = (
	<ChannelItemImage>
		<OpenIcon />
	</ChannelItemImage>
);

const restrictedImage = (
	<ChannelItemImage>
		<RestrictedIcon />
	</ChannelItemImage>
);

const openAccessDataOld = {
	lozengeType: LOZENGE_TYPES.ON,
	lozengeLabel: customerAccessMessages.openLabel,
	image: openImage,
	description: messages.openPermissions,
};
const restrictedAccessDataOld = {
	lozengeType: LOZENGE_TYPES.RESTRICTED,
	lozengeLabel: customerAccessMessages.restrictedLabel,
	image: restrictedImage,
	description: messages.restrictedPermissions,
};

const openAccessData = {
	lozengeType: LOZENGE_TYPES.ON,
	lozengeLabel: customerAccessMessages.openLabel,
	image: <LockOpenIcon label="" color={token('color.icon')} />,
	description: messages.open,
};
const restrictedAccessData = {
	lozengeType: LOZENGE_TYPES.RESTRICTED,
	lozengeLabel: customerAccessMessages.restrictedLabel,
	image: <LockClosedIcon label="" color={token('color.icon')} />,
	description: messages.restricted,
};

const accessTypeMapOld = {
	[CUSTOMER_ACCESS.PUBLIC]: openAccessDataOld,
	[CUSTOMER_ACCESS.INTERNAL]: openAccessDataOld,
	[CUSTOMER_ACCESS.ANONYMOUS]: openAccessDataOld,
	[CUSTOMER_ACCESS.RESTRICTED]: restrictedAccessDataOld,
};

const accessTypeMap = {
	[CUSTOMER_ACCESS.PUBLIC]: openAccessData,
	[CUSTOMER_ACCESS.INTERNAL]: openAccessData,
	[CUSTOMER_ACCESS.ANONYMOUS]: openAccessData,
	[CUSTOMER_ACCESS.RESTRICTED]: restrictedAccessData,
};

const CustomerAccessChannelViewComponent = (props: Props) => {
	const { formatMessage } = useIntl();
	const { accessType, isAdmin, onClick, link } = props;
	const { lozengeType, lozengeLabel, image, description } = getWillShowNav4()
		? accessTypeMap[accessType]
		: accessTypeMapOld[accessType];

	const configureButton = (
		<Button
			href={link}
			onClick={onClick}
			appearance="default"
			{...(fg('jsm_aiiy_for_nav_item_channel')
				? { 'aria-label': formatMessage(messages.configureLabelDescriptive) }
				: {})}
		>
			{formatMessage(messages.configure)}
		</Button>
	);

	const lozenge = (
		<ChannelStatusLozenge
			type={lozengeType}
			label={formatMessage(lozengeLabel).toLocaleUpperCase()}
		/>
	);

	const label = getWillShowNav4()
		? formatMessage(messages.itemLabel)
		: formatMessage(messages.label);

	return (
		<BaseChannelItem
			button={isAdmin ? configureButton : undefined}
			image={image}
			label={label}
			description={formatMessage(description)}
			channelName="customer-access"
			statusLozenge={lozenge}
		/>
	);
};

export const CustomerAccessChannelView = flow(
	withFireUiAnalytics({
		onClick: 'configureCustomerAccess',
	}),
)(CustomerAccessChannelViewComponent);
